import React from 'react'
import { formatDate, formatTime } from '../../../common/utils/appUtils'
import { PAYMENT_MODE_OPTIONS, PRODUCT_DETAIL_FIELDS } from '../../../common/constant/page-constant'
import currencyFormatter from '../../../common/utils/currencyFormatterUtils'
import { formatProductListLabel } from '../../../common/utils/productUtils'

const PharmacyCopyTemplate = ({ invoiceData, branchData }) => {
  const getDiscountAmount = () => Number(invoiceData.discount) || 0

  // VAT amount is now stored directly, not as percentage
  const getVatAmount = () => {
    return Number(invoiceData.vat || 0).toFixed(2)
  }

  const getVatPercentage = () => {
    if (invoiceData.vat_percentage !== undefined && invoiceData.vat_percentage !== null) {
      return invoiceData.vat_percentage
    }
    const baseAmount = (invoiceData.amount || 0) - getDiscountAmount()
    if (baseAmount > 0 && invoiceData.vat > 0) {
      return parseFloat(((invoiceData.vat / baseAmount) * 100).toFixed(2))
    }
    return 0
  }

  const styles = {
    container: {
      fontFamily: "'Poppins', 'Montserrat', sans-serif",
      maxWidth: '300px',
      margin: '0 auto',
      padding: '10px',
      backgroundColor: 'white',
    },
    header: {
      textAlign: 'center',
      marginBottom: '10px',
      borderBottom: '1px dashed #000',
      paddingBottom: '10px',
    },
    copyType: {
      fontSize: '12px',
      fontWeight: 'bold',
      backgroundColor: '#f0f0f0',
      padding: '4px 8px',
      marginBottom: '8px',
      textAlign: 'center',
    },
    logo: {
      maxWidth: '50px',
      maxHeight: '50px',
      margin: '0 auto',
    },
    branchName: {
      fontSize: '16px',
      fontWeight: 'bold',
      margin: '5px 0',
    },
    branchPhone: {
      fontSize: '12px',
      margin: '3px 0',
    },
    branchAddress: {
      fontSize: '10px',
      margin: '3px 0',
    },
    invoiceInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '11px',
      margin: '8px 0',
      borderBottom: '1px dashed #000',
      paddingBottom: '8px',
    },
    customerInfo: {
      fontSize: '11px',
      margin: '8px 0',
      borderBottom: '1px dashed #000',
      paddingBottom: '8px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '10px',
      marginTop: '10px',
    },
    th: {
      borderBottom: '1px solid #000',
      padding: '4px 2px',
      textAlign: 'left',
      fontWeight: 'bold',
    },
    td: {
      padding: '4px 2px',
      borderBottom: '1px dashed #ccc',
    },
    tdRight: {
      padding: '4px 2px',
      borderBottom: '1px dashed #ccc',
      textAlign: 'right',
    },
    tdCenter: {
      padding: '4px 2px',
      borderBottom: '1px dashed #ccc',
      textAlign: 'center',
    },
    summary: {
      marginTop: '10px',
      borderTop: '1px dashed #000',
      paddingTop: '10px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '11px',
      padding: '3px 0',
    },
    totalRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '14px',
      fontWeight: 'bold',
      padding: '5px 0',
      borderTop: '1px solid #000',
      marginTop: '5px',
    },
    paymentMode: {
      marginTop: '10px',
      fontSize: '11px',
    },
    disclaimer: {
      marginTop: '15px',
      fontSize: '9px',
      textAlign: 'center',
      borderTop: '1px dashed #000',
      paddingTop: '10px',
    },
    footer: {
      marginTop: '10px',
      fontSize: '10px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
  }

  return (
    <div style={styles.container} className="invoice-template pharmacy-copy">
      <div style={styles.copyType}>PHARMACY COPY</div>
      
      {/* Header */}
      <div style={styles.header}>
        {branchData?.logo && (
          <img src={branchData.logo} alt="logo" style={styles.logo} />
        )}
        <div style={styles.branchName}>
          {branchData?.pharmacy_name || invoiceData?.branch_details?.pharmacy_name || ''}
          {(branchData?.name || invoiceData?.branch_details?.name) ? ` (${branchData?.name || invoiceData?.branch_details?.name})` : ''}
        </div>
        <div style={styles.branchPhone}>{branchData?.mobile_no || invoiceData?.branch_details?.mobile_no}</div>
        <div style={styles.branchAddress}>{branchData?.address || invoiceData?.branch_details?.address}</div>
      </div>

      {/* Invoice Info */}
      <div style={styles.invoiceInfo}>
        <div>
          <div><strong>Invoice:</strong> {invoiceData.invoice_number}</div>
          <div><strong>Type:</strong> {invoiceData.type === 'IN_STORE' ? 'In Store' : 'Online'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>{formatDate(invoiceData.updated_at || invoiceData.created_at)}</div>
          <div>{formatTime(invoiceData.updated_at || invoiceData.created_at)}</div>
        </div>
      </div>

      {/* Customer Info */}
      <div style={styles.customerInfo}>
        <div><strong>Customer:</strong> {invoiceData?.customer?.name}</div>
        <div><strong>Phone:</strong> {invoiceData?.customer?.mobile_no}</div>
        <div><strong>Address:</strong> {invoiceData?.customer?.shipping_address}</div>
      </div>

      {/* Products Table - Full details for pharmacy */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>SL</th>
            <th style={styles.th}>Product Name (Size)-Strength</th>
            <th style={styles.th}>Category</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Batch</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Exp</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Qty</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Rate</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Amt</th>
          </tr>
        </thead>
        <tbody>
          {invoiceData.product_details?.map((product, index) => {
            const amount = (product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0) * 
                          (product?.[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0)
            const lineLabel = formatProductListLabel(
              {
                name: product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || '',
                size: product?.size || '',
                dose: product?.[PRODUCT_DETAIL_FIELDS.DOSE] || '',
                strength: product?.strength || '',
                category: product?.category || product?.product_category || '',
              },
              { includeCategory: false }
            )
            return (
              <tr key={index}>
                <td style={styles.td}>{index + 1}</td>
                <td style={styles.td}>
                  {lineLabel}
                </td>
                <td style={styles.td}>{product?.category || product?.product_category || ''}</td>
                <td style={styles.tdCenter}>
                  {product?.[PRODUCT_DETAIL_FIELDS.INVENTORY]?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER] || 
                   product?.inventory?.batch_number || '-'}
                </td>
                <td style={styles.tdCenter}>
                  {formatDate(product?.inventory?.expiry_date) || '-'}
                </td>
                <td style={styles.tdCenter}>{product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0}</td>
                <td style={styles.tdRight}>
                  {currencyFormatter(Number(product?.[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0).toFixed(2))}
                </td>
                <td style={styles.tdRight}>
                  {currencyFormatter(Number(amount || 0).toFixed(2))}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Summary */}
      <div style={styles.summary}>
        <div style={styles.summaryRow}>
          <span>Amount (BDT)</span>
          <span>{currencyFormatter(Number(invoiceData.amount || 0).toFixed(2))}</span>
        </div>
        <div style={styles.summaryRow}>
          <span>Discount</span>
          <span>{Number(getDiscountAmount() || 0).toFixed(2)}</span>
        </div>
        <div style={styles.summaryRow}>
          <span>VAT ({getVatPercentage()}%)</span>
          <span>{getVatAmount()}</span>
        </div>
        <div style={styles.totalRow}>
          <span>Total (BDT)</span>
          <span>{currencyFormatter(Number(invoiceData.total_amount || 0).toFixed(2))}</span>
        </div>
        
        {/* Payment Details - Same column alignment */}
        {invoiceData.payment_mode && (
          <>
            <div style={styles.summaryRow}>
              <span>Payment Mode</span>
              <span>{PAYMENT_MODE_OPTIONS.find(item => item.name === invoiceData.payment_mode)?.label || invoiceData.payment_mode}</span>
            </div>
            
            {/* Cash Payment */}
            {invoiceData.payment_mode === 'CASH' && (
              <div style={styles.summaryRow}>
                <span>Paid by Cash</span>
                <span>{Number(invoiceData.paid_amount || invoiceData.total_amount || 0).toFixed(2)}</span>
              </div>
            )}
            
            {/* Card Payment Details */}
            {invoiceData.payment_mode === 'CARD' && (
              <>
                {invoiceData.transaction_fee_percent > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                    <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {invoiceData.final_card_amount && invoiceData.transaction_fee_amount > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Final Card Amt</span>
                    <span>{Number(invoiceData.final_card_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
                  <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                    <span>Total Payable</span>
                    <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Online Payment Details */}
            {invoiceData.payment_mode === 'ONLINE' && (
              <>
                {invoiceData.mfs_name && (
                  <div style={styles.summaryRow}>
                    <span>MFS</span>
                    <span>{invoiceData.mfs_name}</span>
                  </div>
                )}
                {invoiceData.transaction_fee_percent > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                    <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
                  <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                    <span>Total Payable</span>
                    <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Cash & Card Payment Details */}
            {invoiceData.payment_mode === 'CASH_CARD' && (
              <>
                <div style={styles.summaryRow}>
                  <span>Paid by Cash</span>
                  <span>{Number(invoiceData.amount_cash || 0).toFixed(2)}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Paid by Card</span>
                  <span>{Number(invoiceData.amount_card || 0).toFixed(2)}</span>
                </div>
                {invoiceData.transaction_fee_percent > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                    <span>{Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {invoiceData.final_card_amount && invoiceData.transaction_fee_amount > 0 && (
                  <div style={styles.summaryRow}>
                    <span>Final Card Amt</span>
                    <span>{Number(invoiceData.final_card_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                {invoiceData.total_payable && invoiceData.transaction_fee_amount > 0 && (
                  <div style={{...styles.summaryRow, fontWeight: 'bold'}}>
                    <span>Total Payable</span>
                    <span>{Number(invoiceData.total_payable || 0).toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Due Sales */}
            {invoiceData.payment_mode === 'DUE_SALES' && (
              <div style={styles.summaryRow}>
                <span>Due Amount</span>
                <span>{Number(invoiceData.total_amount || 0).toFixed(2)}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Disclaimer */}
      <div style={styles.disclaimer}>
        বিঃদ্রঃ সেলস রিসিট ব্যতীত বিক্রিত পণ্য ফেরত বা পরিবর্তন করা হয় না। 
        ক্রয়কৃত পণ্য ৭ (সাত) দিনের মধ্যে পরিবর্তনযোগ্য। তবে পাতা কাটা ওষুধ, 
        ইন্সুলিন, ভ‍্যাক্সিন এবং সুগার টেস্ট স্ট্রিপ ফেরত বা পরিবর্তনযোগ্য নয়।
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        Software By: DG ePharma | 09606501155
      </div>
    </div>
  )
}

export default PharmacyCopyTemplate

